/**
 * Agreements — the adviser's "Send for Signature" control.
 *
 * One place to: pick an existing client (auto-fills name/email/program) OR type a new
 * person in manually, choose the program (service), optionally override the amount,
 * preview the generated retainer PDF, and send the sign link (email + conversation).
 * Plus a "Sent" list with live status/download, and a "Setup" tab (firm profile,
 * firm signature capture, program catalog, template publish).
 *
 * Backend: /agreements/* on maven-platform-server.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';

// ============================ styled theme ============================
const Page = styled.div`
  height: 100%; overflow-y: auto; padding: 24px 28px; color: #e6e9f0;
  background: linear-gradient(180deg, #0f1419 0%, #121826 100%);
`;
const H1 = styled.h1`font-size: 22px; margin: 0 0 4px; color: #fff;`;
const Sub = styled.p`margin: 0 0 20px; color: #8b93a7; font-size: 14px;`;
const Tabs = styled.div`display: flex; gap: 8px; margin-bottom: 20px; flex-wrap: wrap;`;
const Tab = styled.button`
  padding: 9px 18px; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px;
  border: 1px solid ${p => p.$active ? 'rgba(139,92,246,0.6)' : 'rgba(120,140,180,0.18)'};
  background: ${p => p.$active ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'rgba(255,255,255,0.03)'};
  color: ${p => p.$active ? '#fff' : '#aeb6ca'};
`;
const Card = styled.div`
  background: rgba(255,255,255,0.03); border: 1px solid rgba(120,140,180,0.15);
  border-radius: 14px; padding: 20px; margin-bottom: 16px; backdrop-filter: blur(6px);
`;
const Row = styled.div`display: flex; gap: 14px; flex-wrap: wrap; ${p => p.$mt && 'margin-top:14px;'}`;
const Field = styled.div`flex: 1; min-width: ${p => p.$min || '200px'}; display: flex; flex-direction: column; gap: 6px;`;
const Label = styled.label`font-size: 12px; color: #9aa3ba; font-weight: 600;`;
const Input = styled.input`
  background: rgba(10,14,22,0.7); border: 1px solid rgba(120,140,180,0.2); border-radius: 9px;
  padding: 10px 12px; color: #eef1f7; font-size: 14px; outline: none;
  &:focus { border-color: rgba(139,92,246,0.6); }
`;
const TextArea = styled.textarea`
  background: rgba(10,14,22,0.7); border: 1px solid rgba(120,140,180,0.2); border-radius: 9px;
  padding: 10px 12px; color: #eef1f7; font-size: 14px; outline: none; min-height: 70px; resize: vertical;
  &:focus { border-color: rgba(139,92,246,0.6); }
`;
const Select = styled.select`
  background: rgba(10,14,22,0.7); border: 1px solid rgba(120,140,180,0.2); border-radius: 9px;
  padding: 10px 34px 10px 12px; color: #eef1f7; font-size: 14px; outline: none; cursor: pointer;
  appearance: none; -webkit-appearance: none; -moz-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239aa3ba' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>");
  background-repeat: no-repeat; background-position: right 12px center;
  &:focus { border-color: rgba(139,92,246,0.6); }
  & option { background: #121826; color: #eef1f7; }
`;
// Themed file picker (hides the native "Choose File" button)
const HiddenFile = styled.input`display: none;`;
const FileLabel = styled.label`
  display: inline-block; padding: 9px 16px; border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 600;
  color: #c7cee0; background: rgba(255,255,255,0.06); border: 1px solid rgba(120,140,180,0.25);
  &:hover { background: rgba(139,92,246,0.14); border-color: rgba(139,92,246,0.5); }
`;
const Btn = styled.button`
  padding: 10px 18px; border-radius: 10px; cursor: pointer; font-weight: 600; font-size: 14px; border: none;
  background: ${p => p.$variant === 'ghost' ? 'rgba(255,255,255,0.06)'
    : p.$variant === 'danger' ? 'linear-gradient(135deg,#ef4444,#dc2626)'
    : 'linear-gradient(135deg,#6366f1,#8b5cf6)'};
  color: #fff; opacity: ${p => p.disabled ? 0.5 : 1};
  ${p => p.$variant === 'ghost' && 'border:1px solid rgba(120,140,180,0.2); color:#c7cee0;'}
`;
const Toggle = styled.div`display: inline-flex; background: rgba(10,14,22,0.7); border-radius: 10px; padding: 4px; gap: 4px;`;
const ToggleBtn = styled.button`
  padding: 8px 16px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; font-size: 13px;
  background: ${p => p.$active ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'transparent'};
  color: ${p => p.$active ? '#fff' : '#98a1b6'};
`;
const ClientRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; cursor: pointer;
  border-radius: 8px; border: 1px solid ${p => p.$sel ? 'rgba(139,92,246,0.6)' : 'transparent'};
  background: ${p => p.$sel ? 'rgba(139,92,246,0.12)' : 'rgba(255,255,255,0.02)'}; margin-top: 6px;
  &:hover { background: rgba(139,92,246,0.08); }
`;
const Pill = styled.span`
  font-size: 11px; font-weight: 700; padding: 3px 9px; border-radius: 999px; text-transform: capitalize;
  color: ${p => STATUS_COLORS[p.$s]?.fg || '#cbd2e2'}; background: ${p => STATUS_COLORS[p.$s]?.bg || 'rgba(255,255,255,0.08)'};
`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13.5px;`;
const Th = styled.th`text-align: left; color: #8b93a7; font-weight: 600; padding: 8px 10px; border-bottom: 1px solid rgba(120,140,180,0.15);`;
const Td = styled.td`padding: 10px; border-bottom: 1px solid rgba(120,140,180,0.08); color: #d6dbe8;`;
const Banner = styled.div`
  border: 1px solid rgba(245,158,11,0.4); background: rgba(245,158,11,0.08); color: #fcd982;
  border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; font-size: 14px;
`;
const Iframe = styled.iframe`width: 100%; height: 68vh; border: none; border-radius: 10px; background: #fff;`;

// themed modal (replaces native window.prompt / confirm)
const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(6,9,15,0.66); backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; z-index: 4000;
`;
const Modal = styled.div`
  width: 100%; max-width: 460px; background: #141a28; border: 1px solid rgba(120,140,180,0.22);
  border-radius: 16px; padding: 22px 24px; box-shadow: 0 24px 60px rgba(0,0,0,0.5);
`;
const ModalTitle = styled.h3`margin: 0 0 6px; font-size: 17px; color: #fff;`;
const ModalText = styled.p`margin: 0 0 14px; font-size: 13.5px; color: #9aa3ba; line-height: 1.5;`;
const ModalActions = styled.div`display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px;`;

const STATUS_COLORS = {
  draft:   { fg: '#cbd2e2', bg: 'rgba(148,163,184,0.18)' },
  sent:    { fg: '#93c5fd', bg: 'rgba(59,130,246,0.18)' },
  viewed:  { fg: '#c4b5fd', bg: 'rgba(139,92,246,0.18)' },
  signed:  { fg: '#86efac', bg: 'rgba(34,197,94,0.18)' },
  declined:{ fg: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
  expired: { fg: '#fcd34d', bg: 'rgba(245,158,11,0.18)' },
  cancelled:{ fg: '#cbd2e2', bg: 'rgba(148,163,184,0.18)' },
  voided:  { fg: '#fca5a5', bg: 'rgba(239,68,68,0.18)' },
};

// ============================ signature pad ============================
function SignaturePad({ onChange, height = 160 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const pts = useRef([]);          // array of stroke arrays of {x,y}
  const cur = useRef(null);

  const ctx = () => canvasRef.current.getContext('2d');
  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; cur.current = []; pts.current.push(cur.current); const p = pos(e); cur.current.push(p); const c = ctx(); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const p = pos(e); cur.current.push(p);
    const c = ctx(); c.lineWidth = 2; c.lineCap = 'round'; c.strokeStyle = '#111';
    c.lineTo(p.x, p.y); c.stroke();
  };
  const end = () => { if (!drawing.current) return; drawing.current = false; emit(); };

  const emit = () => {
    const w = canvasRef.current.width, h = canvasRef.current.height;
    const d = pts.current.filter(s => s.length).map(s =>
      'M ' + s.map(p => `${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' L ')
    ).join(' ');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><path fill="none" stroke="#000" stroke-width="2" d="${d}"/></svg>`;
    onChange(d ? { svg, width: w, height: h } : null);
  };
  const clear = () => { pts.current = []; const c = ctx(); c.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); onChange(null); };

  useEffect(() => {
    const cv = canvasRef.current;
    cv.width = cv.offsetWidth; cv.height = height;
  }, [height]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height, background: '#fff', borderRadius: 10, border: '1px solid rgba(120,140,180,0.3)', touchAction: 'none', cursor: 'crosshair' }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div style={{ marginTop: 8 }}>
        <Btn $variant="ghost" type="button" onClick={clear}>Clear</Btn>
      </div>
    </div>
  );
}

// ============================ main ============================
export default function Agreements() {
  const api = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [tab, setTab] = useState('send');

  // setup readiness
  const [firm, setFirm] = useState(null);
  const [services, setServices] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadSetup = useCallback(async () => {
    try {
      const [f, s, t] = await Promise.all([
        api.get('/agreements/firm'),
        api.get('/agreements/services'),
        api.get('/agreements/templates'),
      ]);
      setFirm(f.data.data);
      setServices(s.data.data || []);
      setTemplates(t.data.data || []);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { loadSetup(); }, [loadSetup]);

  const publishedTemplate = templates.find(t => t.status === 'published');
  const hasSignature = firm?.firmSignature?.hasSignature;
  const setupComplete = !!publishedTemplate && !!hasSignature && services.length > 0;

  return (
    <Page>
      <H1>Agreements</H1>
      <Sub>Generate a retainer for a client, let them review and sign it, and keep the signed copy.</Sub>

      {!setupComplete && (
        <Banner>
          Finish setup before sending: {[
            services.length === 0 && 'add a program',
            !publishedTemplate && 'publish a template',
            !hasSignature && 'add your firm signature',
          ].filter(Boolean).join(' · ')}. Go to the <b>Setup</b> tab.
        </Banner>
      )}

      <Tabs>
        <Tab $active={tab === 'send'} onClick={() => setTab('send')}>Send for signature</Tab>
        <Tab $active={tab === 'sent'} onClick={() => setTab('sent')}>Sent</Tab>
        <Tab $active={tab === 'templates'} onClick={() => setTab('templates')}>Templates</Tab>
        <Tab $active={tab === 'setup'} onClick={() => setTab('setup')}>Setup</Tab>
      </Tabs>

      {tab === 'send' && (
        <SendTab api={api} showToast={showToast} services={services} setupComplete={setupComplete} onSent={() => setTab('sent')} />
      )}
      {tab === 'sent' && <SentTab api={api} showToast={showToast} confirm={confirm} />}
      {tab === 'templates' && (
        <TemplatesTab api={api} showToast={showToast} confirm={confirm} reload={loadSetup} />
      )}
      {tab === 'setup' && (
        <SetupTab api={api} showToast={showToast} confirm={confirm} firm={firm} services={services} templates={templates} reload={loadSetup} />
      )}
    </Page>
  );
}

// ============================ SEND ============================
function SendTab({ api, showToast, services, setupComplete, onSent }) {
  const [mode, setMode] = useState('existing');       // 'existing' | 'manual'
  const [q, setQ] = useState('');
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);      // {threadId,name,email,...}
  const [manualName, setManualName] = useState('');
  const [manualEmail, setManualEmail] = useState('');

  const [serviceId, setServiceId] = useState('');
  const [overrideFee, setOverrideFee] = useState('');
  const [overrideDeposit, setOverrideDeposit] = useState('');
  const [overrideBalance, setOverrideBalance] = useState('');

  const [instance, setInstance] = useState(null);
  const [draftUrl, setDraftUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const service = services.find(s => s._id === serviceId);

  useEffect(() => {
    if (mode !== 'existing') return;
    const t = setTimeout(async () => {
      try { const r = await api.get(`/agreements/clients?q=${encodeURIComponent(q)}`); setClients(r.data.data || []); }
      catch (e) { console.error(e); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, mode, api]);

  const clientReady = mode === 'existing' ? !!selected : (manualName.trim() && manualEmail.trim());

  const generate = async () => {
    if (!serviceId) return showToast('Pick a program', 'warning');
    if (!clientReady) return showToast('Choose or enter a client', 'warning');
    setBusy(true);
    try {
      const body = {
        serviceOfferingId: serviceId,
        professionalFee: overrideFee, depositAmount: overrideDeposit, balanceAmount: overrideBalance,
      };
      if (mode === 'existing') { body.threadId = selected.threadId; body.clientEmail = selected.email; }
      else { body.clientName = manualName.trim(); body.clientEmail = manualEmail.trim(); }
      const r = await api.post('/agreements/instances', body);
      setInstance(r.data.data.instance);
      setDraftUrl(r.data.data.draftUrl);
      showToast('Draft generated — review it below', 'success');
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to generate', 'error');
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!instance) return;
    setBusy(true);
    try {
      const r = await api.post(`/agreements/instances/${instance._id}/send`);
      showToast(r.data.data.emailed ? 'Sent — client emailed the sign link' : 'Sent (email skipped: no address)', 'success');
      reset(); onSent();
    } catch (e) {
      showToast(e.response?.data?.message || 'Failed to send', 'error');
    } finally { setBusy(false); }
  };

  const reset = () => {
    setInstance(null); setDraftUrl(''); setSelected(null); setManualName(''); setManualEmail('');
    setOverrideFee(''); setOverrideDeposit(''); setOverrideBalance(''); setQ('');
  };

  return (
    <>
      <Card>
        <Label style={{ marginBottom: 10, display: 'block' }}>1 · Who is signing?</Label>
        <Toggle>
          <ToggleBtn $active={mode === 'existing'} onClick={() => { setMode('existing'); setSelected(null); }}>Existing client</ToggleBtn>
          <ToggleBtn $active={mode === 'manual'} onClick={() => { setMode('manual'); setSelected(null); }}>New / manual</ToggleBtn>
        </Toggle>

        {mode === 'existing' ? (
          <div style={{ marginTop: 12 }}>
            <Input placeholder="Search your clients by name or email…" value={q} onChange={e => setQ(e.target.value)} style={{ width: '100%' }} />
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 4 }}>
              {clients.map(c => (
                <ClientRow key={c.conversationId} $sel={selected?.conversationId === c.conversationId} onClick={() => setSelected(c)}>
                  <span><b>{c.name}</b> {c.email && <span style={{ color: '#8b93a7' }}>· {c.email}</span>}</span>
                  {selected?.conversationId === c.conversationId && <span style={{ color: '#8b5cf6' }}>✓</span>}
                </ClientRow>
              ))}
              {q && clients.length === 0 && <div style={{ color: '#8b93a7', padding: 10, fontSize: 13 }}>No clients found — switch to "New / manual".</div>}
            </div>
          </div>
        ) : (
          <Row $mt>
            <Field><Label>Client name</Label><Input value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Full name" /></Field>
            <Field><Label>Client email</Label><Input value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder="email@example.com" /></Field>
          </Row>
        )}
      </Card>

      <Card>
        <Label style={{ marginBottom: 10, display: 'block' }}>2 · Program &amp; amount</Label>
        <Row>
          <Field><Label>Program</Label>
            <Select value={serviceId} onChange={e => setServiceId(e.target.value)}>
              <option value="">Select a program…</option>
              {services.map(s => <option key={s._id} value={s._id}>{s.displayName}</option>)}
            </Select>
          </Field>
        </Row>
        {service && (
          <Row $mt>
            <Field $min="150px"><Label>Professional fee ({service.currency})</Label>
              <Input type="number" value={overrideFee} onChange={e => setOverrideFee(e.target.value)} placeholder={`${service.professionalFee} (default)`} /></Field>
            <Field $min="150px"><Label>Deposit</Label>
              <Input type="number" value={overrideDeposit} onChange={e => setOverrideDeposit(e.target.value)} placeholder={`${service.depositAmount} (default)`} /></Field>
            <Field $min="150px"><Label>Balance</Label>
              <Input type="number" value={overrideBalance} onChange={e => setOverrideBalance(e.target.value)} placeholder={`${service.balanceAmount} (default)`} /></Field>
          </Row>
        )}
        <div style={{ marginTop: 16 }}>
          <Btn onClick={generate} disabled={busy || !setupComplete}>{busy ? 'Working…' : 'Generate preview'}</Btn>
        </div>
      </Card>

      {instance && draftUrl && (
        <Card>
          <Label style={{ marginBottom: 10, display: 'block' }}>3 · Review the exact document, then send</Label>
          <Iframe title="Agreement preview" src={draftUrl} />
          <Row $mt>
            <Btn onClick={send} disabled={busy}>Send for signature</Btn>
            <Btn $variant="ghost" onClick={reset} disabled={busy}>Discard</Btn>
          </Row>
        </Card>
      )}
    </>
  );
}

// ============================ SENT ============================
function SentTab({ api, showToast, confirm }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voidRow, setVoidRow] = useState(null);      // opens the styled void modal
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/agreements/instances'); setList(r.data.data || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const open = async (id) => {
    try {
      const r = await api.get(`/agreements/instances/${id}/download`);
      window.open(r.data.data.url, '_blank');
    } catch (e) { showToast('Could not open document', 'error'); }
  };
  const cancel = async (row) => {
    if (!(await confirm({ title: 'Cancel agreement', message: `Cancel "${row.title}"? The client will no longer be able to sign it.`, confirmText: 'Cancel agreement', cancelText: 'Keep it', type: 'warning' }))) return;
    try { await api.post(`/agreements/instances/${row._id}/cancel`); showToast('Cancelled', 'success'); load(); }
    catch (e) { showToast(e.response?.data?.message || 'Failed', 'error'); }
  };
  const doVoid = async () => {
    if (!voidRow) return;
    setVoiding(true);
    try {
      await api.post(`/agreements/instances/${voidRow._id}/void`, { reason: voidReason.trim() });
      showToast('Agreement voided', 'success');
      setVoidRow(null); setVoidReason(''); load();
    } catch (e) { showToast(e.response?.data?.message || 'Failed to void', 'error'); }
    finally { setVoiding(false); }
  };

  if (loading) return <Card>Loading…</Card>;
  if (!list.length) return <Card>No agreements yet. Create one from the “Send for signature” tab.</Card>;

  return (
    <Card>
      <Table>
        <thead><tr>
          <Th>Client</Th><Th>Document</Th><Th>Status</Th><Th>Created</Th><Th></Th>
        </tr></thead>
        <tbody>
          {list.map(r => (
            <tr key={r._id}>
              <Td>{r.clientName || '—'}{r.clientEmail && <div style={{ color: '#8b93a7', fontSize: 12 }}>{r.clientEmail}</div>}</Td>
              <Td>{r.title}</Td>
              <Td><Pill $s={r.status}>{r.status}</Pill></Td>
              <Td>{new Date(r.createdAt).toLocaleDateString()}</Td>
              <Td style={{ whiteSpace: 'nowrap' }}>
                <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => open(r._id)}>Open</Btn>{' '}
                {['draft', 'sent', 'viewed'].includes(r.status) &&
                  <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => cancel(r)}>Cancel</Btn>}
                {r.status === 'signed' &&
                  <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => { setVoidReason(''); setVoidRow(r); }}>Void</Btn>}
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      {voidRow && (
        <Overlay onClick={() => !voiding && setVoidRow(null)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Void this signed agreement?</ModalTitle>
            <ModalText>
              This marks <b>“{voidRow.title}”</b> for {voidRow.clientName || 'the client'} as <b>voided</b>. The signed
              record is kept for audit, but the agreement no longer stands. This can’t be undone.
            </ModalText>
            <Label style={{ marginBottom: 6, display: 'block' }}>Reason (optional)</Label>
            <TextArea value={voidReason} onChange={e => setVoidReason(e.target.value)} placeholder="e.g. superseded by a corrected agreement" style={{ width: '100%' }} />
            <ModalActions>
              <Btn $variant="ghost" onClick={() => setVoidRow(null)} disabled={voiding}>Cancel</Btn>
              <Btn $variant="danger" onClick={doVoid} disabled={voiding}>{voiding ? 'Voiding…' : 'Void agreement'}</Btn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </Card>
  );
}

// ============================ SETUP ============================
function SetupTab({ api, showToast, firm, services, templates, reload }) {
  const [profile, setProfile] = useState({ firmName: '', rcicNumber: '', addressLines: '', email: '', phone: '' });
  const [logoName, setLogoName] = useState('');
  const [footerName, setFooterName] = useState('');
  const [sigName, setSigName] = useState('');
  const [sig, setSig] = useState(null);

  useEffect(() => {
    if (firm?.firmProfile) {
      setProfile({
        firmName: firm.firmProfile.firmName || '', rcicNumber: firm.firmProfile.rcicNumber || '',
        addressLines: (firm.firmProfile.addressLines || []).join('\n'),
        email: firm.firmProfile.email || '', phone: firm.firmProfile.phone || '',
      });
    }
    if (firm?.firmSignature?.name) setSigName(firm.firmSignature.name);
  }, [firm]);

  const saveProfile = async () => {
    try {
      await api.put('/agreements/firm/profile', {
        ...profile, addressLines: profile.addressLines.split('\n').map(s => s.trim()).filter(Boolean),
      });
      showToast('Firm profile saved', 'success'); reload();
    } catch (e) { showToast('Failed to save profile', 'error'); }
  };
  const saveSignature = async () => {
    if (!sig) return showToast('Draw the firm signature first', 'warning');
    try {
      await api.put('/agreements/firm/signature', { name: sigName, svg: sig.svg });
      showToast('Firm signature saved', 'success'); reload();
    } catch (e) { showToast('Failed to save signature', 'error'); }
  };
  const publishedTemplate = templates.find(t => t.status === 'published');

  return (
    <>
      <Card>
        <Label style={{ fontSize: 15, color: '#fff', marginBottom: 12, display: 'block' }}>Firm profile</Label>
        <Row>
          <Field><Label>Firm name (“The Firm”)</Label><Input value={profile.firmName} onChange={e => setProfile({ ...profile, firmName: e.target.value })} /></Field>
          <Field $min="150px"><Label>RCIC #</Label><Input value={profile.rcicNumber} onChange={e => setProfile({ ...profile, rcicNumber: e.target.value })} /></Field>
        </Row>
        <Row $mt>
          <Field><Label>Letterhead address (one line each)</Label><TextArea value={profile.addressLines} onChange={e => setProfile({ ...profile, addressLines: e.target.value })} /></Field>
        </Row>
        <Row $mt>
          <Field><Label>Email</Label><Input value={profile.email} onChange={e => setProfile({ ...profile, email: e.target.value })} /></Field>
          <Field><Label>Phone</Label><Input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} /></Field>
        </Row>
        <Row $mt>
          <Field>
            <Label>Firm logo (PNG/JPG — shown on the letterhead)</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <HiddenFile id="firm-logo-upload" type="file" accept="image/png,image/jpeg"
                onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  setLogoName(f.name);
                  const r = new FileReader();
                  r.onload = () => setProfile(p => ({ ...p, logoDataUrl: r.result }));
                  r.readAsDataURL(f);
                }} />
              <FileLabel htmlFor="firm-logo-upload">Choose logo…</FileLabel>
              <span style={{ color: '#9aa3ba', fontSize: 13 }}>{logoName || 'No file chosen'}</span>
              {(profile.logoDataUrl || firm?.firmProfile?.logoDataUrl) &&
                <span style={{ color: '#86efac', fontSize: 12 }}>✓ Logo {profile.logoDataUrl ? 'ready to save' : 'on file'}</span>}
            </div>
          </Field>
        </Row>
        <Row $mt>
          <Field>
            <Label>Footer image (PNG/JPG — centered at the bottom, e.g. a regulator badge)</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <HiddenFile id="firm-footer-upload" type="file" accept="image/png,image/jpeg"
                onChange={e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  setFooterName(f.name);
                  const r = new FileReader();
                  r.onload = () => setProfile(p => ({ ...p, footerImageDataUrl: r.result }));
                  r.readAsDataURL(f);
                }} />
              <FileLabel htmlFor="firm-footer-upload">Choose footer image…</FileLabel>
              <span style={{ color: '#9aa3ba', fontSize: 13 }}>{footerName || 'No file chosen'}</span>
              {(profile.footerImageDataUrl || firm?.firmProfile?.footerImageDataUrl) &&
                <span style={{ color: '#86efac', fontSize: 12 }}>✓ Footer {profile.footerImageDataUrl ? 'ready to save' : 'on file'}</span>}
            </div>
          </Field>
        </Row>
        <div style={{ marginTop: 14 }}><Btn onClick={saveProfile}>Save profile</Btn></div>
      </Card>

      <Card>
        <Label style={{ fontSize: 15, color: '#fff', marginBottom: 4, display: 'block' }}>Firm signature</Label>
        <Sub>Drawn once and auto-stamped onto every agreement your clients sign.</Sub>
        {firm?.firmSignature?.hasSignature && <div style={{ color: '#86efac', fontSize: 13, marginBottom: 10 }}>✓ A firm signature is on file. Draw again to replace it.</div>}
        <Row><Field $min="200px"><Label>Signer name (printed under the line)</Label><Input value={sigName} onChange={e => setSigName(e.target.value)} placeholder="e.g. Rita Khalfon" /></Field></Row>
        <div style={{ marginTop: 12, maxWidth: 520 }}><SignaturePad onChange={setSig} /></div>
        <div style={{ marginTop: 12 }}><Btn onClick={saveSignature}>Save signature</Btn></div>
      </Card>

      <Card>
        <Label style={{ fontSize: 15, color: '#fff', marginBottom: 8, display: 'block' }}>Templates</Label>
        {publishedTemplate
          ? <div style={{ color: '#86efac', fontSize: 13 }}>✓ {templates.filter(t => t.status === 'published').length} published template(s). Manage or edit the wording in the <b>Templates</b> tab.</div>
          : <div style={{ color: '#fcd982', fontSize: 13 }}>No published template yet. Go to the <b>Templates</b> tab to edit and publish one before sending.</div>}
      </Card>

      <ServicesEditor api={api} showToast={showToast} services={services} templates={templates} reload={reload} />
    </>
  );
}

// ============================ TEMPLATES ============================
const BLOCK_KINDS = ['heading', 'paragraph', 'listItem', 'clauseSlot', 'feeTable', 'feeNotes', 'signatureBlock', 'spacer'];
const TEXT_KINDS = ['heading', 'paragraph', 'listItem'];
const AUTO_KINDS = { feeTable: 'Government-fee table (from the program)', feeNotes: 'Program fee notes', signatureBlock: 'Client + firm signature block', spacer: 'Blank spacing' };

function BlockEditor({ blocks, onChange }) {
  const set = (i, patch) => onChange(blocks.map((b, idx) => idx === i ? { ...b, ...patch } : b));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= blocks.length) return; const c = [...blocks]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  const del = (i) => onChange(blocks.filter((_, idx) => idx !== i));
  const add = () => onChange([...(blocks || []), { kind: 'paragraph', text: '' }]);

  return (
    <div>
      {(blocks || []).map((b, i) => (
        <div key={i} style={{ border: '1px solid rgba(120,140,180,0.18)', borderRadius: 10, padding: 10, marginBottom: 8, background: 'rgba(255,255,255,0.02)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select value={b.kind} onChange={e => set(i, { kind: e.target.value })} style={{ padding: '7px 30px 7px 10px', fontSize: 13 }}>
              {BLOCK_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </Select>
            {b.kind === 'listItem' && <Input placeholder="marker (1. a. •)" value={b.marker || ''} onChange={e => set(i, { marker: e.target.value })} style={{ width: 110, padding: '7px 10px' }} />}
            {(b.kind === 'listItem' || b.kind === 'heading') && <Input type="number" placeholder="lvl" value={b.level || ''} onChange={e => set(i, { level: e.target.value ? +e.target.value : undefined })} style={{ width: 66, padding: '7px 10px' }} />}
            {b.kind === 'clauseSlot' && <Input placeholder="slot key (refund)" value={b.slotKey || ''} onChange={e => set(i, { slotKey: e.target.value })} style={{ width: 150, padding: '7px 10px' }} />}
            <span style={{ flex: 1 }} />
            <Btn $variant="ghost" style={{ padding: '5px 10px' }} onClick={() => move(i, -1)}>↑</Btn>
            <Btn $variant="ghost" style={{ padding: '5px 10px' }} onClick={() => move(i, 1)}>↓</Btn>
            <Btn $variant="danger" style={{ padding: '5px 10px' }} onClick={() => del(i)}>✕</Btn>
          </div>
          {TEXT_KINDS.includes(b.kind) &&
            <TextArea value={b.text || ''} onChange={e => set(i, { text: e.target.value })} placeholder="Text… wrap **like this** for bold. Use {{clientName}}, {{professionalFee}}, {{depositAmount}}, {{balanceAmount}}, {{applicationLabel}}, {{firmName}}, {{rcicNumber}}." style={{ width: '100%', marginTop: 8, minHeight: 54 }} />}
          {AUTO_KINDS[b.kind] && <div style={{ color: '#8b93a7', fontSize: 12, marginTop: 6 }}>↳ {AUTO_KINDS[b.kind]}</div>}
          {b.kind === 'clauseSlot' && <div style={{ color: '#8b93a7', fontSize: 12, marginTop: 6 }}>↳ Filled by the clause whose slot + the program's variant match (see Clauses below).</div>}
        </div>
      ))}
      <Btn $variant="ghost" onClick={add}>+ Add block</Btn>
    </div>
  );
}

function ClauseEditor({ clauses, onChange }) {
  const set = (i, patch) => onChange(clauses.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const del = (i) => onChange(clauses.filter((_, idx) => idx !== i));
  const add = () => onChange([...(clauses || []), { slotKey: 'refund', variant: 'workPermit', label: '', blocks: [] }]);

  return (
    <div>
      {(clauses || []).map((c, i) => (
        <div key={i} style={{ border: '1px solid rgba(120,140,180,0.2)', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <Row>
            <Field $min="140px"><Label>Slot key</Label><Input value={c.slotKey} onChange={e => set(i, { slotKey: e.target.value })} placeholder="refund" /></Field>
            <Field $min="140px"><Label>Variant</Label><Input value={c.variant} onChange={e => set(i, { variant: e.target.value })} placeholder="workPermit / pr" /></Field>
            <Field><Label>Label (for you)</Label><Input value={c.label || ''} onChange={e => set(i, { label: e.target.value })} placeholder="Refund — Work Permit" /></Field>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}><Btn $variant="danger" style={{ padding: '8px 12px' }} onClick={() => del(i)}>Remove</Btn></div>
          </Row>
          <div style={{ marginTop: 10 }}><Label style={{ marginBottom: 6, display: 'block' }}>Clause content</Label>
            <BlockEditor blocks={c.blocks || []} onChange={bl => set(i, { blocks: bl })} />
          </div>
        </div>
      ))}
      <Btn $variant="ghost" onClick={add}>+ Add clause variant</Btn>
    </div>
  );
}

function TemplatesTab({ api, showToast, confirm, reload }) {
  const [list, setList] = useState([]);
  const [library, setLibrary] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [sel, setSel] = useState(null);          // editable copy of the selected template
  const [preview, setPreview] = useState('');
  const [libPreview, setLibPreview] = useState(null);   // { name, url } for the gallery preview modal
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/agreements/templates'); setList(r.data.data || []); }
    catch (e) { console.error(e); }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/agreements/template-library').then(r => setLibrary(r.data.data || [])).catch(() => {}); }, [api]);

  const editable = sel && sel.status === 'draft';

  const select = (t) => { setPreview(''); setSel(JSON.parse(JSON.stringify(t))); };

  const copyFromLibrary = async (lib) => {
    setBusy(true);
    try {
      const r = await api.post('/agreements/templates/from-library', { libraryKey: lib.key });
      setShowLibrary(false); await load(); select(r.data.data);
      showToast(`Copied "${lib.name}" — customize it below`, 'success');
    } catch (e) { showToast(e.response?.data?.message || 'Failed to copy', 'error'); }
    finally { setBusy(false); }
  };

  const duplicate = async (t) => {
    setBusy(true);
    try {
      const r = await api.post(`/agreements/templates/${t._id}/duplicate`, {});
      await load(); select(r.data.data);
      showToast('Duplicated — edit your copy', 'success');
    } catch (e) { showToast(e.response?.data?.message || 'Failed to duplicate', 'error'); }
    finally { setBusy(false); }
  };

  const previewLibrary = async (lib) => {
    setBusy(true);
    try {
      const r = await api.post(`/agreements/template-library/${lib.key}/preview`, {});
      const bytes = Uint8Array.from(atob(r.data.data.pdfBase64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setLibPreview({ name: lib.name, url });
    } catch (e) { showToast(e.response?.data?.message || 'Preview failed', 'error'); }
    finally { setBusy(false); }
  };
  const closeLibPreview = () => { if (libPreview) URL.revokeObjectURL(libPreview.url); setLibPreview(null); };

  const create = async () => {
    if (!newName.trim()) return showToast('Give the template a name', 'warning');
    setBusy(true);
    try {
      const r = await api.post('/agreements/templates', { name: newName.trim(), blocks: [{ kind: 'heading', level: 1, text: newName.trim().toUpperCase() }, { kind: 'paragraph', text: 'By and between {{clientName}} (referred to as "The Client")' }, { kind: 'signatureBlock' }] });
      setNewName(''); await load(); select(r.data.data);
      showToast('Template created — edit it below', 'success');
    } catch (e) { showToast(e.response?.data?.message || 'Failed to create', 'error'); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/agreements/templates/${sel._id}`, { name: sel.name, blocks: sel.blocks, clauses: sel.clauses, consentText: sel.consentText });
      showToast('Saved', 'success'); await load();
    } catch (e) { showToast(e.response?.data?.message || 'Failed to save', 'error'); }
    finally { setBusy(false); }
  };

  const doPreview = async () => {
    setBusy(true);
    try {
      if (editable) await api.put(`/agreements/templates/${sel._id}`, { name: sel.name, blocks: sel.blocks, clauses: sel.clauses, consentText: sel.consentText });
      const r = await api.post(`/agreements/templates/${sel._id}/preview`, {});
      // Blob URL (not a data: URL) so the iframe reliably renders the PDF.
      const bytes = Uint8Array.from(atob(r.data.data.pdfBase64), c => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      setPreview(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) { showToast(e.response?.data?.message || 'Preview failed', 'error'); }
    finally { setBusy(false); }
  };

  const publish = async () => {
    if (!(await confirm({ title: 'Publish template', message: 'Publish this version? It becomes the version new agreements use, and can no longer be edited.', confirmText: 'Publish', type: 'info' }))) return;
    setBusy(true);
    try { await api.post(`/agreements/templates/${sel._id}/publish`); showToast('Published', 'success'); await load(); const r = await api.get(`/agreements/templates/${sel._id}`); select(r.data.data); reload && reload(); }
    catch (e) { showToast(e.response?.data?.message || 'Failed to publish', 'error'); }
    finally { setBusy(false); }
  };

  const newVersion = async () => {
    setBusy(true);
    try { const r = await api.post(`/agreements/templates/${sel._id}/version`); await load(); select(r.data.data); showToast('New editable draft created', 'success'); }
    catch (e) { showToast(e.response?.data?.message || 'Failed', 'error'); }
    finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!(await confirm({ title: 'Delete draft', message: `Delete draft "${t.name}" v${t.version}?`, confirmText: 'Delete', type: 'warning' }))) return;
    try { await api.delete(`/agreements/templates/${t._id}`); if (sel?._id === t._id) setSel(null); load(); showToast('Deleted', 'success'); }
    catch (e) { showToast(e.response?.data?.message || 'Failed', 'error'); }
  };

  return (
    <>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <Label style={{ fontSize: 15, color: '#fff' }}>Your templates</Label>
          <Btn onClick={() => setShowLibrary(v => !v)}>{showLibrary ? 'Hide starter gallery' : 'Start from a template'}</Btn>
        </div>

        {showLibrary && (
          <div style={{ marginBottom: 16 }}>
            <Banner>Starter drafts — editable starting points, not legal advice. Review and adapt the wording (especially legal clauses, jurisdiction and fees) before use.</Banner>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {library.map(lib => (
                <div key={lib.key} style={{ border: '1px solid rgba(120,140,180,0.18)', borderRadius: 12, padding: 14, background: 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 }}>{lib.category}</div>
                  <div style={{ fontWeight: 700, color: '#eef1f7', margin: '4px 0 6px' }}>{lib.name}</div>
                  <div style={{ fontSize: 12.5, color: '#9aa3ba', flex: 1, lineHeight: 1.5 }}>{lib.description}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <Btn $variant="ghost" style={{ flex: 1 }} disabled={busy} onClick={() => previewLibrary(lib)}>Preview</Btn>
                    <Btn style={{ flex: 1 }} disabled={busy} onClick={() => copyFromLibrary(lib)}>Use this</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <Table style={{ marginBottom: 14 }}>
          <thead><tr><Th>Name</Th><Th>Family</Th><Th>Version</Th><Th>Status</Th><Th></Th></tr></thead>
          <tbody>
            {list.map(t => (
              <tr key={t._id}>
                <Td>{t.name}</Td><Td style={{ color: '#8b93a7' }}>{t.templateKey}</Td><Td>v{t.version}</Td>
                <Td><Pill $s={t.status === 'published' ? 'signed' : t.status === 'draft' ? 'sent' : 'cancelled'}>{t.status}</Pill></Td>
                <Td style={{ whiteSpace: 'nowrap' }}>
                  <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => select(t)}>{t.status === 'draft' ? 'Edit' : 'View'}</Btn>{' '}
                  <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => duplicate(t)} disabled={busy}>Duplicate</Btn>{' '}
                  {t.status === 'draft' && <Btn $variant="ghost" style={{ padding: '6px 12px' }} onClick={() => del(t)}>Delete</Btn>}
                </Td>
              </tr>
            ))}
            {!list.length && <tr><Td colSpan={5} style={{ color: '#8b93a7' }}>No templates yet — use “Start from a template” above, or create a blank one below.</Td></tr>}
          </tbody>
        </Table>
        <Row>
          <Field><Label>New blank template name</Label><Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Engagement Letter" /></Field>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}><Btn $variant="ghost" onClick={create} disabled={busy}>Create blank</Btn></div>
        </Row>
      </Card>

      {sel && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Label style={{ fontSize: 15, color: '#fff' }}>{editable ? 'Editing' : 'Viewing'}: {sel.name} · v{sel.version} <Pill $s={sel.status === 'published' ? 'signed' : 'sent'} style={{ marginLeft: 8 }}>{sel.status}</Pill></Label>
            <Btn $variant="ghost" onClick={() => setSel(null)}>Close</Btn>
          </div>

          {!editable && <div style={{ color: '#fcd982', fontSize: 13, marginBottom: 12 }}>This version is published (read-only). Click “Create new version” to make an editable copy.</div>}

          <fieldset disabled={!editable} style={{ border: 'none', padding: 0, margin: 0, opacity: editable ? 1 : 0.7 }}>
            <Row>
              <Field><Label>Name</Label><Input value={sel.name} onChange={e => setSel({ ...sel, name: e.target.value })} /></Field>
            </Row>
            <div style={{ marginTop: 16 }}><Label style={{ fontSize: 14, color: '#dfe4ee', marginBottom: 8, display: 'block' }}>Document body</Label>
              <BlockEditor blocks={sel.blocks || []} onChange={bl => setSel({ ...sel, blocks: bl })} />
            </div>
            <div style={{ marginTop: 20 }}><Label style={{ fontSize: 14, color: '#dfe4ee', marginBottom: 8, display: 'block' }}>Clause variants (per program)</Label>
              <ClauseEditor clauses={sel.clauses || []} onChange={cl => setSel({ ...sel, clauses: cl })} />
            </div>
            <div style={{ marginTop: 16 }}>
              <Field><Label>Consent line (shown to the client when signing)</Label>
                <TextArea value={sel.consentText || ''} onChange={e => setSel({ ...sel, consentText: e.target.value })} style={{ width: '100%' }} /></Field>
            </div>
          </fieldset>

          <Row $mt>
            {editable && <Btn onClick={save} disabled={busy}>Save draft</Btn>}
            <Btn $variant="ghost" onClick={doPreview} disabled={busy}>{busy ? 'Working…' : 'Preview PDF'}</Btn>
            {editable && <Btn onClick={publish} disabled={busy}>Publish</Btn>}
            {!editable && <Btn onClick={newVersion} disabled={busy}>Create new version</Btn>}
          </Row>

          {preview && <Iframe title="Template preview" src={preview} style={{ marginTop: 16 }} />}
        </Card>
      )}

      {libPreview && (
        <Overlay onClick={closeLibPreview}>
          <Modal onClick={e => e.stopPropagation()} style={{ maxWidth: 820, width: '92%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <ModalTitle style={{ margin: 0 }}>{libPreview.name} — preview</ModalTitle>
              <Btn $variant="ghost" onClick={closeLibPreview}>Close</Btn>
            </div>
            <Iframe title="Starter preview" src={libPreview.url} style={{ height: '64vh' }} />
            <ModalActions>
              <Btn onClick={() => { const name = libPreview.name; closeLibPreview(); const lib = library.find(l => l.name === name); if (lib) copyFromLibrary(lib); }}>Use this template</Btn>
            </ModalActions>
          </Modal>
        </Overlay>
      )}
    </>
  );
}

// ---- program (service offering) editor ----
function ServicesEditor({ api, showToast, services, templates, reload }) {
  const blank = { displayName: '', applicationLabel: '', currency: 'CAD', professionalFee: 0, depositAmount: 0, balanceAmount: 0, clauseVariant: 'workPermit', templateKey: '', govFees: [] };
  const [draft, setDraft] = useState(blank);
  // Distinct published template families to choose from.
  const templateFamilies = Array.from(new Map((templates || []).filter(t => t.status === 'published').map(t => [t.templateKey, t.name])).entries());

  const save = async () => {
    if (!draft.displayName) return showToast('Program name is required', 'warning');
    try {
      await api.post('/agreements/services', draft);
      showToast('Program added', 'success'); setDraft(blank); reload();
    } catch (e) { showToast(e.response?.data?.message || 'Failed to add program', 'error'); }
  };

  return (
    <Card>
      <Label style={{ fontSize: 15, color: '#fff', marginBottom: 12, display: 'block' }}>Programs (fees &amp; clause)</Label>
      {services.length > 0 && (
        <Table style={{ marginBottom: 16 }}>
          <thead><tr><Th>Name</Th><Th>Fee</Th><Th>Clause</Th><Th>Template</Th><Th>Active</Th></tr></thead>
          <tbody>
            {services.map(s => (
              <tr key={s._id}><Td>{s.displayName}</Td><Td>{s.currency} {s.professionalFee}</Td><Td>{s.clauseVariant}</Td><Td style={{ color: '#8b93a7' }}>{s.templateKey || 'default'}</Td><Td>{s.active ? '✓' : '—'}</Td></tr>
            ))}
          </tbody>
        </Table>
      )}
      <Row>
        <Field><Label>Program name</Label><Input value={draft.displayName} onChange={e => setDraft({ ...draft, displayName: e.target.value })} placeholder="LMIA with Work Permit" /></Field>
        <Field><Label>Application label (shown in doc)</Label><Input value={draft.applicationLabel} onChange={e => setDraft({ ...draft, applicationLabel: e.target.value })} placeholder="LMIA WITH WORK PERMIT" /></Field>
      </Row>
      <Row $mt>
        <Field $min="120px"><Label>Fee</Label><Input type="number" value={draft.professionalFee} onChange={e => setDraft({ ...draft, professionalFee: +e.target.value })} /></Field>
        <Field $min="120px"><Label>Deposit</Label><Input type="number" value={draft.depositAmount} onChange={e => setDraft({ ...draft, depositAmount: +e.target.value })} /></Field>
        <Field $min="120px"><Label>Balance</Label><Input type="number" value={draft.balanceAmount} onChange={e => setDraft({ ...draft, balanceAmount: +e.target.value })} /></Field>
        <Field $min="140px"><Label>Refund clause</Label>
          <Select value={draft.clauseVariant} onChange={e => setDraft({ ...draft, clauseVariant: e.target.value })}>
            <option value="workPermit">workPermit</option>
            <option value="pr">pr</option>
          </Select>
        </Field>
        <Field $min="160px"><Label>Template</Label>
          <Select value={draft.templateKey} onChange={e => setDraft({ ...draft, templateKey: e.target.value })}>
            <option value="">Default template</option>
            {templateFamilies.map(([key, name]) => <option key={key} value={key}>{name} ({key})</option>)}
          </Select>
        </Field>
      </Row>
      <div style={{ marginTop: 14 }}><Btn onClick={save}>Add program</Btn></div>
    </Card>
  );
}
